// src/services/dataService.js
import API from '../api/config';
import { cacheService, CACHE_KEYS, invalidateCacheOnModification } from './cacheService';

class DataService {
  constructor() {
    this.user = null;
    this.isInitialized = false;
  }

  initialize(user) {
    this.user = user;
    this.isInitialized = true;
  }

  getUser() {
    if (!this.user) {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        try {
          this.user = JSON.parse(userStr);
        } catch (e) {
          console.error('Failed to parse user:', e);
        }
      }
    }
    return this.user;
  }

  async fetchWithCache(cacheKey, apiCall, ttl = 30 * 60 * 1000, forceRefresh = false) {
    if (forceRefresh) {
      cacheService.remove(cacheKey);
    }

    const cached = cacheService.get(cacheKey);
    if (cached !== null && cached !== undefined) {
      return cached;
    }

    try {
      const response = await apiCall();
      let data = response?.data;
      
      if (data === null || data === undefined) {
        cacheService.set(cacheKey, [], ttl);
        return [];
      }
      
      if (Array.isArray(data)) {
        cacheService.set(cacheKey, data, ttl);
        return data;
      }
      
      if (data?.data && Array.isArray(data.data)) {
        cacheService.set(cacheKey, data.data, ttl);
        return data.data;
      }
      
      if (data?.data && typeof data.data === 'object') {
        const arrayData = Object.values(data.data).filter(item => item && typeof item === 'object');
        cacheService.set(cacheKey, arrayData, ttl);
        return arrayData;
      }
      
      if (data?.Status === true && data?.data) {
        if (Array.isArray(data.data)) {
          cacheService.set(cacheKey, data.data, ttl);
          return data.data;
        }
      }
      
      if (data?.msg && Array.isArray(data.msg)) {
        cacheService.set(cacheKey, data.msg, ttl);
        return data.msg;
      }
      
      cacheService.set(cacheKey, [], ttl);
      return [];
    } catch (error) {
      console.error(`Error fetching ${cacheKey}:`, error);
      const expiredCache = cacheService.get(cacheKey);
      if (expiredCache !== null && expiredCache !== undefined) {
        return expiredCache;
      }
      return [];
    }
  }

  // ===================== MASTER DATA APIS =====================

  // Get Makes (Tyre Make)
  async getMakes(forceRefresh = false) {
    const user = this.getUser();
    if (!user?.access_token) return [];
    return this.fetchWithCache(
      CACHE_KEYS.MAKES,
      () => API.get('getMake', { headers: { Authorization: `Bearer ${user.access_token}` } }),
      30 * 60 * 1000,
      forceRefresh
    );
  }

  // Get Truck Makes
  async getTruckMakes(forceRefresh = false) {
    const user = this.getUser();
    if (!user?.access_token) return [];
    return this.fetchWithCache(
      CACHE_KEYS.TRUCK_MAKES,
      () => API.get('gettruckmake', { headers: { Authorization: `Bearer ${user.access_token}` } }),
      30 * 60 * 1000,
      forceRefresh
    );
  }

  // Get Status Categories
  async getStatusCategories(pageName = '', forceRefresh = false) {
    const user = this.getUser();
    if (!user?.access_token) return [];
    const cacheKey = pageName ? `${CACHE_KEYS.STATUS_CATEGORIES}_${pageName}` : CACHE_KEYS.STATUS_CATEGORIES;
    return this.fetchWithCache(
      cacheKey,
      () => API.get('get_tyrestatuscategory', {
        params: pageName ? { page_name: pageName } : {},
        headers: { Authorization: `Bearer ${user.access_token}` }
      }),
      30 * 60 * 1000,
      forceRefresh
    );
  }

  // Get Position Types
  async getPositionTypes(forceRefresh = false) {
    const user = this.getUser();
    if (!user?.access_token) return [];
    return this.fetchWithCache(
      CACHE_KEYS.POSITION_TYPES,
      () => API.get('getPositionType', { headers: { Authorization: `Bearer ${user.access_token}` } }),
      30 * 60 * 1000,
      forceRefresh
    );
  }

  // Get Projects
  async getProjects(forceRefresh = false) {
    const user = this.getUser();
    if (!user?.access_token) return [];
    return this.fetchWithCache(
      CACHE_KEYS.PROJECTS,
      () => API.get('getsiteProjectFor_SiteAdmin', {
        params: {
          login_user_id: user.userid,
          role_id: user.role_id,
          parent_id: user.parent_id || null,
        },
        headers: { Authorization: `Bearer ${user.access_token}` }
      }),
      30 * 60 * 1000,
      forceRefresh
    );
  }

  // Get Used Tyre Types
  async getUsedTyreTypes(forceRefresh = false) {
    const user = this.getUser();
    if (!user?.access_token) return [];
    return this.fetchWithCache(
      CACHE_KEYS.USED_TYRE_TYPES,
      () => API.get('usedtyretypelist', { headers: { Authorization: `Bearer ${user.access_token}` } }),
      30 * 60 * 1000,
      forceRefresh
    );
  }

  // Get Wear Types
  async getWearTypes(forceRefresh = false) {
    const user = this.getUser();
    if (!user?.access_token) return [];
    return this.fetchWithCache(
      CACHE_KEYS.WEAR_TYPES,
      () => API.get('typeofwearlist', { headers: { Authorization: `Bearer ${user.access_token}` } }),
      30 * 60 * 1000,
      forceRefresh
    );
  }

  // Get Sites
  async getSites(forceRefresh = false) {
    const user = this.getUser();
    if (!user?.access_token) return [];
    return this.fetchWithCache(
      CACHE_KEYS.SITES,
      () => API.get('getsitenameforfilter', {
        params: {
          login_user_id: user.userid,
          role_id: user.role_id,
          parent_id: user.parent_id || null
        },
        headers: { Authorization: `Bearer ${user.access_token}` }
      }),
      30 * 60 * 1000,
      forceRefresh
    );
  }

  // Get Tyre Sizes
  async getTyreSizes(forceRefresh = false) {
    const user = this.getUser();
    if (!user?.access_token) return [];
    return this.fetchWithCache(
      CACHE_KEYS.TYRE_SIZES,
      () => API.get('getTyreSizeForCustomerDashboard', { headers: { Authorization: `Bearer ${user.access_token}` } }),
      30 * 60 * 1000,
      forceRefresh
    );
  }

  // Get Tyre Types by Make ID
  async getTyreTypesByMake(makeId, forceRefresh = false) {
    const user = this.getUser();
    if (!user?.access_token || !makeId) return [];
    const cacheKey = `${CACHE_KEYS.TYRE_TYPES}_${makeId}`;
    return this.fetchWithCache(
      cacheKey,
      () => API.get(`gettyretype?tyre_make_id=${makeId}`, { headers: { Authorization: `Bearer ${user.access_token}` } }),
      30 * 60 * 1000,
      forceRefresh
    );
  }

  // Get Tyre Sizes by Type ID
  async getTyreSizesByType(typeId, forceRefresh = false) {
    const user = this.getUser();
    if (!user?.access_token || !typeId) return [];
    const cacheKey = `cached_tyre_sizes_by_type_${typeId}`;
    return this.fetchWithCache(
      cacheKey,
      () => API.get(`getTyreTypeTyreSize?tyretype_id=${typeId}`, { headers: { Authorization: `Bearer ${user.access_token}` } }),
      30 * 60 * 1000,
      forceRefresh
    );
  }

  // Get Truck Models by Make ID
  async getTruckModelsByMake(makeId, forceRefresh = false) {
    const user = this.getUser();
    if (!user?.access_token || !makeId) return [];
    const cacheKey = `cached_truck_models_${makeId}`;
    return this.fetchWithCache(
      cacheKey,
      () => API.get(`getTruckMakeTruckModel?truckmakeid=${makeId}`, { headers: { Authorization: `Bearer ${user.access_token}` } }),
      30 * 60 * 1000,
      forceRefresh
    );
  }

  // Get Positions by Type
  async getPositionsByType(positionType, forceRefresh = false) {
    const user = this.getUser();
    if (!user?.access_token || !positionType) return [];
    const cacheKey = `cached_positions_${positionType}`;
    return this.fetchWithCache(
      cacheKey,
      () => API.get(`getPosition?possition_type=${positionType}`, { headers: { Authorization: `Bearer ${user.access_token}` } }),
      30 * 60 * 1000,
      forceRefresh
    );
  }

  // ===================== TYRE ENTRY DATA =====================

  // Get Tyre Entries - FIXED: Added missing method
  async getTyreEntries(forceRefresh = false) {
    const user = this.getUser();
    if (!user?.userid) return [];
    
    const cacheKey = CACHE_KEYS.TYRE_ENTRIES;
    
    if (forceRefresh) {
      cacheService.remove(cacheKey);
    }

    return this.fetchWithCache(
      cacheKey,
      () => API.get('tyreEntryList', {
        params: {
          login_user_id: user.userid,
          role_id: user.role_id,
          parent_id: user.parent_id,
        },
        headers: { Authorization: `Bearer ${user.access_token}` }
      }),
      5 * 60 * 1000, // 5 minutes for dynamic data
      forceRefresh
    );
  }

  // ===================== INVALIDATION =====================

  invalidate(type) {
    invalidateCacheOnModification(type);
  }

  // Invalidate all master data cache
  invalidateAllMasterData() {
    const masterKeys = [
      CACHE_KEYS.SITES,
      CACHE_KEYS.MAKES,
      CACHE_KEYS.TYRE_SIZES,
      CACHE_KEYS.STATUS_CATEGORIES,
      CACHE_KEYS.POSITION_TYPES,
      CACHE_KEYS.PROJECTS,
      CACHE_KEYS.USED_TYRE_TYPES,
      CACHE_KEYS.WEAR_TYPES,
      CACHE_KEYS.TRUCK_MAKES,
      CACHE_KEYS.TYRE_TYPES,
      CACHE_KEYS.TYRE_ENTRIES,
    ];
    masterKeys.forEach(key => cacheService.remove(key));
    cacheService.clearByPattern('cached_tyre_sizes_by_type');
    cacheService.clearByPattern('cached_truck_models');
    cacheService.clearByPattern('cached_positions_');
    cacheService.clearByPattern('cached_total_');
    console.log('✅ All master data cache invalidated');
  }
}

export const dataService = new DataService();